/**
 * The phone's FRIENDS view — `design/Games Tonight Mobile.dc.html` :633-694.
 *
 * THE PHONE HAS NO INLINE PANEL AT ANY WIDTH. This is the whole crew surface for
 * a game on this tree: verdict, roster, the call strip, comments and composer,
 * or — when the game is not on the list — the one dashed add target and the note
 * saying what being on the list unlocks. Desktop's `DetailFriendsPanel` renders
 * the same information as a third column; this is not a narrow version of it.
 *
 * IT READS ITS OWN VIEW-MODEL rather than taking one as a prop, because it is
 * mounted from `MobileApp`'s overlay block beside every other sheet, not from
 * the detail screen. `s.detailId` is the game it is about, and `openGame` clears
 * `friendsOpen` — without that, opening game B from inside this sheet would land
 * on B with A's crew still on screen, which is the prototype's own live bug (its
 * nav never clears the flag).
 *
 * THE ROSTER CARRIES THE LIBRARY COLUMN, which the phone mirror does not draw at
 * all. Both mirrors lost it in the export; the V2 changelog entry specifies the
 * row as "avatar · name · their call · library state", and the phone roster is
 * new here, so it ships the same four states the desktop one does rather than
 * inheriting the export's omission.
 *
 * THE VERDICT BAND IS INERT HERE. On desktop that bar doubles as the panel's
 * collapse toggle; there is nothing to collapse on a full-screen view, so it is
 * a heading and nothing else.
 */

import { Avatar } from '../components/Avatar.tsx';
import { FullSheet } from '../components/FullSheet.tsx';
import { PixelButton } from '../components/PixelButton.tsx';
import { useQd } from '../store/index.ts';
import { NO_STEAM_TIP, quickAddBlocked } from '../store/selectors.3c.ts';
import { detailVotesRows } from '../store/selectors.desktop.ts';
import type { DetailVoteRow } from '../store/selectors.desktop.ts';
import { detailVM } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';

/** One roster: avatar · name · the call over the ownership line (call blank off the list). */
function Roster({ rows }: { rows: DetailVoteRow[] }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid var(--gt-line)', flex: 'none' }}>
      {rows.map((r) => (
        <div
          key={r.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            borderBottom: '1px solid var(--gt-line-0)',
          }}
        >
          <Avatar style={{ flex: 'none' }} size={24} fs={7.5} ini={r.ini} bg={r.hueBg} hasAv={r.hasAv} avBg={r.avBg} />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: MONO,
              fontSize: 10,
              color: 'var(--gt-text-2)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {r.name}
          </span>
          {/* Call over library state, as on desktop and for the same
              reason: 390px does not hold `B · I COULD BE CONVINCED`
              beside `NO STEAM LINK` beside a name. */}
          <span style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-end', flex: 'none' }}>
            {r.lvlTxt ? (
              <span style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '.05em', color: r.lvlClr, whiteSpace: 'nowrap' }}>
                {r.lvlTxt}
              </span>
            ) : null}
            {/* Blank until the ownership RPC answers — an unanswered
                question must never paint as an answer. */}
            {r.libTxt ? (
              <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.05em', color: r.libClr, whiteSpace: 'nowrap' }}>
                {r.libTxt}
              </span>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function FriendsSheet(): JSX.Element | null {
  const s = useQd();
  const d = detailVM(s, s.detailId);
  if (!d) return null;

  const rows = detailVotesRows(s, d.id);
  // PORTING-NOTES D-02: a CheapShark-only row has no appid the list FK can hold.
  // The phone screen's primary CTA carries the same gate; this is the second
  // target, so it needs the same answer rather than a silent no-op.
  const addBlocked = !d.inList && quickAddBlocked(d.id);

  return (
    <FullSheet
      label="Party sheet"
      title={d.inList ? 'PARTY' : 'NOT ON THE LIST'}
      onClose={() => s.closeFriends()}
    >
      {d.inList ? (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '14px 12px',
              background: 'var(--gt-raised)',
              borderBottom: '1px solid var(--gt-border)',
              flex: 'none',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--gt-font-pixel)',
                fontSize: 12,
                background: d.groupBg,
                color: 'var(--gt-ink)',
                padding: '10px 12px',
                transform: 'translateY(0.13em)',
              }}
            >
              {d.groupBig}
            </div>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div
                style={{
                  fontFamily: 'var(--gt-font-pixel)',
                  fontSize: 9,
                  lineHeight: 1.5,
                  color: 'var(--gt-text)',
                  transform: 'translateY(0.13em)',
                }}
              >
                {d.groupSub}
              </div>
              <div style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.06em', color: 'var(--gt-muted)' }}>
                {d.calledTxt}
              </div>
            </div>
          </div>

          <Roster rows={rows} />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 9,
              padding: 12,
              borderBottom: '1px solid var(--gt-line)',
              flex: 'none',
            }}
          >
            <span style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: 7, color: 'var(--acc)' }}>YOUR CALL</span>
            <div style={{ display: 'flex' }}>
              {d.callCells.map((c) => (
                <div
                  key={c.key}
                  onClick={() => void s.vote(d.id, c.key)}
                  data-tip={c.tip}
                  style={{
                    position: c.pos,
                    zIndex: c.z,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // 52 here against desktop's 46: this is a touch target, and
                    // 46 is marginal against the 44px floor once the 1px joins
                    // are taken off.
                    height: 52,
                    marginLeft: c.ml,
                    border: '1px solid ' + c.bd,
                    background: c.bg,
                    color: c.clr,
                    fontFamily: 'var(--gt-font-pixel)',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ transform: 'translateY(0.13em)' }}>{c.g}</span>
                </div>
              ))}
            </div>
            <span style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '.06em', color: d.myClr }}>{d.myTxt}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '13px 12px 18px' }}>
            <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.11em', color: 'var(--gt-muted)' }}>
              {d.commentsTxt}
            </span>
            {d.comments.map((c) => (
              <div key={c.key} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                <Avatar style={{ flex: 'none' }} size={24} fs={7.5} ini={c.ini} bg={c.hueBg} hasAv={c.hasAv} avBg={c.avBg} />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ fontFamily: MONO, fontSize: 9.5, color: 'var(--gt-text-5)' }}>
                    {c.name} <span style={{ color: 'var(--gt-faint)' }}>· {c.agoTxt}</span>
                  </div>
                  <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--gt-text-2)', overflowWrap: 'anywhere' }}>
                    {c.parts.map((p) =>
                      p.isLink ? (
                        <a key={p.key} href={p.href} target="_blank" rel="noreferrer">
                          {p.txt}
                        </a>
                      ) : (
                        <span key={p.key}>{p.txt}</span>
                      ),
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* The short placeholder, as on desktop — the long guidance form
                  was retired with V2 rather than reverted. */}
              <textarea
                value={s.draft}
                onChange={(e) => s.setDraft(e.target.value)}
                placeholder="ADD A COMMENT…"
                rows={2}
                style={{
                  background: 'var(--gt-field)',
                  border: '1px solid var(--gt-border-2)',
                  color: 'var(--gt-text)',
                  fontFamily: MONO,
                  fontSize: 11.5,
                  padding: 12,
                  outline: 'none',
                  resize: 'vertical',
                }}
              />
              <PixelButton label="POST ▸" onClick={() => void s.postC(d.id, s.draft)} fs={8.5} pad="18px 14px" center />
            </div>
          </div>
          {/* The desktop's foot, at last (2026-09-03): arms, then removes for
              the whole party. */}
          <div
            onClick={() => {
              if (!s.listRmArm) {
                s.setListRmArm(true);
                return;
              }
              s.setListRmArm(false);
              void s.removeFromList(d.id);
            }}
            style={{
              height: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderTop: '1px solid var(--gt-line)',
              fontFamily: MONO,
              fontSize: 10,
              letterSpacing: '.07em',
              color: s.listRmArm ? 'var(--acc2)' : 'var(--gt-text-5)',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            {s.listRmArm ? 'SURE?' : "✕ REMOVE FROM TONIGHT'S LIST"}
          </div>
        </>
      ) : (
        <>
        {/* WHO OWNS IT lives behind the PARTY button (2026-09-03): the same
            roster, ownership only, above the add CTA. Adding does not close
            the sheet — it flips in place to the roster, YOUR CALL and comments. */}
        <Roster rows={rows} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13, alignItems: 'center', padding: '20px 14px 22px' }}>
          <div
            onClick={() => {
              if (addBlocked) return;
              void s.quickAdd(d.id);
            }}
            data-tip={addBlocked ? NO_STEAM_TIP : undefined}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 11,
              border: '1px dashed var(--gt-border-3)',
              padding: '24px 12px',
              boxSizing: 'border-box',
              cursor: addBlocked ? 'not-allowed' : 'pointer',
              opacity: addBlocked ? 0.55 : 1,
            }}
          >
            <span style={{ color: 'var(--acc)', fontFamily: MONO, fontSize: 22, lineHeight: 1 }}>+</span>
            <span
              style={{
                fontFamily: 'var(--gt-font-pixel)',
                fontSize: 8.5,
                color: 'var(--acc)',
                transform: 'translateY(0.13em)',
              }}
            >
              ADD TO GAMES TONIGHT?
            </span>
          </div>
        </div>
        </>
      )}
    </FullSheet>
  );
}
